import React, { useState, useEffect, useCallback } from 'react';
import {
    fetchDeviceRecords,
    findUserByEmail,
    createDeviceShare,
    deleteDeviceShare,
    deleteFileShare,
    fetchDeviceShares,
    fetchFileShares,
    getAuthUserId,
} from '../services/api';

// Display helpers pulling from PocketBase's expanded relations / denormalized
// fields, with graceful fallbacks to the raw id.
const granteeLabel = (row) => row?.expand?.grantee?.email || row?.expand?.grantee?.name || row?.grantee || '(unknown user)';
const deviceLabel = (row, deviceNameById) =>
    (deviceNameById && deviceNameById[row?.device]) ||
    row?.deviceName ||
    row?.expand?.device?.username ||
    row?.device ||
    '(unknown device)';

const sectionStyle = { marginBottom: '24px' };
const headingStyle = { fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b7280', marginBottom: '10px' };
const rowStyle = { display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', borderRadius: '6px', fontSize: '0.85rem', color: '#374151' };
const revokeButtonStyle = { background: '#fff5f5', color: '#d32f2f', border: '1px solid #f0caca', borderRadius: '5px', padding: '3px 8px', fontSize: '12px', cursor: 'pointer' };
const primaryButtonStyle = { background: 'rgba(212, 175, 55, 0.15)', color: '#8a6d1a', border: '1px solid rgba(212, 175, 55, 0.5)', borderRadius: '6px', padding: '6px 12px', fontSize: '13px', cursor: 'pointer' };
const inputStyle = { padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' };

const Sharing = ({ deviceNameById }) => {
    const myUserId = getAuthUserId();
    const [devices, setDevices] = useState([]);
    const [deviceShares, setDeviceShares] = useState([]);
    const [fileShares, setFileShares] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState('');
    const [granteeEmail, setGranteeEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState(null);

    const reload = useCallback(async () => {
        const [ds, fs] = await Promise.all([fetchDeviceShares(), fetchFileShares()]);
        setDeviceShares(ds);
        setFileShares(fs);
    }, []);

    useEffect(() => {
        fetchDeviceRecords().then((res) => setDevices(res.items || [])).catch(() => setDevices([]));
        reload().catch(() => { /* ignore */ });
    }, [reload]);

    // Only the shares this user granted are managed here; shares received show up
    // inline in the Devices / Streams tabs instead.
    const grantedByMeDevices = deviceShares.filter((r) => r.sharer === myUserId);
    const grantedByMeFiles = fileShares.filter((r) => r.sharer === myUserId);

    const handleCreateDeviceShare = async () => {
        setStatus(null);
        if (!selectedDevice) { setStatus({ error: 'Pick a device to share.' }); return; }
        if (!granteeEmail.trim()) { setStatus({ error: 'Enter the email of the user to share with.' }); return; }
        setBusy(true);
        try {
            const user = await findUserByEmail(granteeEmail.trim());
            if (!user) { setStatus({ error: `No user found with email "${granteeEmail.trim()}".` }); return; }
            const device = devices.find((d) => d.id === selectedDevice);
            const deviceName = (deviceNameById && deviceNameById[selectedDevice]) || device?.username || selectedDevice;
            await createDeviceShare(selectedDevice, user.id, { deviceUsername: device?.username || '', deviceName });
            setStatus({ ok: `Shared device with ${user.email || granteeEmail.trim()}.` });
            setGranteeEmail('');
            await reload();
        } catch (err) {
            setStatus({ error: err.message });
        } finally {
            setBusy(false);
        }
    };

    const handleRevokeDevice = async (id) => {
        if (!window.confirm('Revoke this device share?')) return;
        await deleteDeviceShare(id);
        await reload();
    };
    const handleRevokeFile = async (id) => {
        if (!window.confirm('Revoke this file share?')) return;
        await deleteFileShare(id);
        await reload();
    };

    return (
        <div>
            {/* Create a device share */}
            <div style={sectionStyle}>
                <div style={headingStyle}>Share a device</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '10px' }}>
                    Grants read-only access to every recording of the device — including ones made later.
                    Individual files can be shared from the right-click menu in the Streams tab.
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                    <select style={inputStyle} value={selectedDevice} onChange={(e) => setSelectedDevice(e.target.value)}>
                        <option value="">Select device…</option>
                        {devices.map((d) => (
                            <option key={d.id} value={d.id}>
                                {(deviceNameById && deviceNameById[d.id]) || d.username || d.id}
                            </option>
                        ))}
                    </select>
                    <input
                        style={inputStyle}
                        type="email"
                        placeholder="Grantee email"
                        value={granteeEmail}
                        onChange={(e) => setGranteeEmail(e.target.value)}
                    />
                    <button style={primaryButtonStyle} disabled={busy} onClick={handleCreateDeviceShare}>
                        {busy ? 'Sharing…' : '🔗 Share device'}
                    </button>
                </div>
                {status && (
                    <div style={{ marginTop: '8px', fontSize: '0.8rem', color: status.error ? '#d32f2f' : '#2e7d32' }}>
                        {status.error || status.ok}
                    </div>
                )}
            </div>

            {/* Shared by me */}
            <div style={sectionStyle}>
                <div style={headingStyle}>Shared by me</div>
                {grantedByMeDevices.length === 0 && grantedByMeFiles.length === 0 && (
                    <div style={{ fontSize: '0.8rem', color: '#9ca3af' }}>You haven't shared anything.</div>
                )}
                {grantedByMeDevices.map((row) => (
                    <div key={row.id} style={rowStyle}>
                        📁 Device <strong>{deviceLabel(row, deviceNameById)}</strong> → {granteeLabel(row)}
                        <button style={revokeButtonStyle} onClick={() => handleRevokeDevice(row.id)}>Revoke</button>
                    </div>
                ))}
                {grantedByMeFiles.map((row) => (
                    <div key={row.id} style={rowStyle}>
                        📄 <strong>{row.fileName}</strong> → {granteeLabel(row)}
                        <button style={revokeButtonStyle} onClick={() => handleRevokeFile(row.id)}>Revoke</button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Sharing;
